import { useNavigate, useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../../backend/src/auth/permissions/permission';
import { SubsetSuggestionStatus } from '../../../../../../../../backend/src/matter/subset/subset-suggestion/subset-suggestion.entity';
import { useUpdateSuggestionStatus } from '@/api/mutations/useUpdateSuggestionStatus';
import { useGetSuggestedSubsets } from '@/api/queries/useGetSuggestedSubsets';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';

export function SubsetSuggestedSubset() {
  const { matterId } = useParams<{ matterId: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useHasPermission();
  const { data: suggestions, isLoading } = useGetSuggestedSubsets();
  const { mutate: updateStatus, isLoading: isUpdating } = useUpdateSuggestionStatus();

  const handleCreateSubset = (suggestionId: string) => {
    navigate(`/matters/${matterId}/subsets/new?suggestionId=${suggestionId}`);
  };

  if (isLoading) {
    return (
      <div className="mb-4 flex w-full gap-2 overflow-x-auto">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-17 w-1/3 rounded-md" />
        ))}
      </div>
    );
  }

  if (!hasPermission(Permission.SubsetCreate) || !suggestions?.length) {
    return null;
  }

  return (
    <div className="mb-4 flex w-full gap-2 overflow-x-auto">
      {suggestions.map((suggestion) => (
        <div key={suggestion.id} className="group relative rounded-md border border-gray-200 bg-gray-50 px-4 py-2 pr-2">
          <div className="flex items-center justify-between gap-5 whitespace-nowrap">
            <div>
              <div className="mb-1 flex items-center gap-1.5">
                <Icon className="lucide--sparkles text-sm text-blue-600" />
                <h3 className="text-sm font-medium text-gray-900">{suggestion.name}</h3>
              </div>
              <p className="text-xs text-gray-600">{suggestion.description}</p>
            </div>

            <div className="flex flex-col items-end">
              <Button
                size="sm"
                className="bg-white"
                onClick={() => handleCreateSubset(suggestion.id)}
                disabled={isUpdating}
              >
                <Icon className="lucide--plus" />
                Create subset
              </Button>

              <Button
                size="sm"
                variant="ghost"
                className="text-gray-500"
                onClick={() =>
                  updateStatus({
                    suggestionId: suggestion.id,
                    status: 'dismissed' as SubsetSuggestionStatus.Dismissed,
                  })
                }
                disabled={isUpdating}
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
